import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { zervantSettingsQueryKey } from '../../../../invoicingSettings/constants/zervantSettingsQueryKey'
import { useUpdateZervantSettings } from '../../../../invoicingSettings/hooks/useUpdateZervantSettings'
import { useZervantSettings } from '../../../../invoicingSettings/hooks/useZervantSettings'
import { ProductUnitCategory } from '../../../enums/productUnitCategory'
import type { ProductUnit } from '../../../types/productUnit'
import { getProductUnitCategory } from '../../../utils/getProductUnitCategory'
import { getProductUnitName } from '../../../utils/getProductUnitName'
import type { ProductUnitOnSaveParams } from '../../ProductUnitsModal'

const isNamedCustomUnit = (unit: ProductUnit): unit is ProductUnit & { name: string } =>
  !!unit.name && getProductUnitCategory(unit) === ProductUnitCategory.Custom

const getCustomUnitNames = (units: ProductUnit[]): string[] => units.filter(isNamedCustomUnit).map((unit) => unit.name)

interface UseSaveProductUnitsOptions {
  isValueConfigured: boolean
  onClose: () => void
  onSelect: (unit: string) => void
  onUnitAdded: (unit: string) => void
  value: string | undefined
}

export const useSaveProductUnits = (options: UseSaveProductUnitsOptions) => {
  const { isValueConfigured, onClose, onSelect, onUnitAdded, value } = options

  const { t } = useTranslation()
  const { data: zervantSettings } = useZervantSettings()
  const { update, isProcessing } = useUpdateZervantSettings()

  const saveProductUnits = useCallback(
    (params: ProductUnitOnSaveParams) => {
      if (!zervantSettings || !zervantSettings?.managedFields || isProcessing) {
        return
      }

      const currentCustomUnitNames = getCustomUnitNames(zervantSettings.managedFields.UNIT_OPTIONS)
      const customUnitNames = getCustomUnitNames(params.units)
      const hasUnitsChanged =
        customUnitNames.length !== currentCustomUnitNames.length ||
        customUnitNames.some((name) => !currentCustomUnitNames.includes(name))

      if (!hasUnitsChanged) {
        onClose()
        return
      }

      const isValueDeleted = isValueConfigured && !params.units.some((unit) => getProductUnitName(unit, t) === value)
      const keptLastUnitAdded = params.units.find((unit) => unit.name === params.lastUnitAdded)?.name

      update(
        {
          managedFields: {
            ...zervantSettings.managedFields,
            // The backend always returns the standard catalog and turns every UNITS entry into a custom unit
            UNITS: customUnitNames,
            UNIT_OPTIONS: params.units,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(zervantSettingsQueryKey)
            notify({
              id: NotificationKeys.ProductUnitsUpdated,
              message: t('products.product_units_modal.notification.success'),
              variant: 'success',
            })

            // Cleared to an empty string, not undefined, because the invoice line schema requires a string
            if (keptLastUnitAdded) {
              onSelect(keptLastUnitAdded)
              onUnitAdded(keptLastUnitAdded)
            } else if (isValueDeleted) {
              onSelect('')
            }

            onClose()
          },
          onError: () => {
            notify({
              id: NotificationKeys.ProductUnitsUpdated,
              message: t('something_went_wrong'),
              variant: 'error',
            })
          },
        },
      )
    },
    [isProcessing, isValueConfigured, onClose, onSelect, onUnitAdded, t, update, value, zervantSettings],
  )

  return { saveProductUnits }
}
